/**
 * 版本更新记录 Hook
 * 获取和缓存更新记录数据
 */

import { useState, useEffect, useCallback } from 'react';
import type { Changelog, ChangelogEntry } from '@/types/changelog';

interface UseChangelogResult {
  changelog: Changelog | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  getLatestVersion: () => ChangelogEntry | null;
}

// 缓存
let cachedChangelog: Changelog | null = null;

export const useChangelog = (): UseChangelogResult => {
  const [changelog, setChangelog] = useState<Changelog | null>(cachedChangelog);
  const [loading, setLoading] = useState(!cachedChangelog);
  const [error, setError] = useState<string | null>(null);

  const fetchChangelog = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/changelog.json');
      if (!response.ok) {
        throw new Error(`Failed to fetch changelog: ${response.status}`);
      }

      const data: Changelog = await response.json();
      cachedChangelog = data;
      setChangelog(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      console.error('Failed to load changelog:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!cachedChangelog) {
      fetchChangelog();
    }
  }, [fetchChangelog]);

  const getLatestVersion = useCallback((): ChangelogEntry | null => {
    if (!changelog?.versions?.length) {
      return null;
    }
    return changelog.versions[0];
  }, [changelog]);

  return {
    changelog,
    loading,
    error,
    refresh: fetchChangelog,
    getLatestVersion,
  };
};

export default useChangelog;
